import React, { useEffect, useState } from 'react';

function Admin({ adminToken }) {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [data, setData] = useState({
    turno_actual: null,
    turnos_actuales: [],
    modo: 'fila',
    lista_espera: [],
    columnas: [],
    rangos_mesa: [],
    llamados: [],
    cantidad_puestos: 1,
    en_espera: 0,
    atendidos_hoy: 0,
    cancelados_hoy: 0,
    espera_promedio_min: null,
    ritmo_atencion_min: null,
    nombre_local: '',
    sucursal: '',
    client_url: '',
    display_url: '',
    activar_subscripcion_disponible: false,
    cancelar_subscripcion_disponible: false,
    cancelacion_programada: false,
    dias_restantes_subscripcion: null,
    payment_provider_url: ''
  });

  const [error, setError] = useState('');
  const [activating, setActivating] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const apiBase = adminToken ? `/api/admin/${adminToken}` : null;

  // Caja elegida por ESTE dispositivo (persistida en el navegador).
  // Restricción de UI: evita tocar por error el botón de otra caja.
  // Valores: número de caja, 'todas' (modo dueño) o null (sin elegir).
  const cajaStorageKey = `miturno_caja_${adminToken}`;
  const [cajaSeleccionada, setCajaSeleccionada] = useState(() => {
    try {
      const guardada = window.localStorage.getItem(`miturno_caja_${adminToken}`);
      if (guardada === 'todas') return 'todas';
      const numero = Number(guardada);
      return Number.isInteger(numero) && numero > 0 ? numero : null;
    } catch (e) {
      return null;
    }
  });

  const elegirCaja = (valor) => {
    try {
      window.localStorage.setItem(cajaStorageKey, String(valor));
    } catch (e) {}
    setCajaSeleccionada(valor);
  };

  const cambiarCaja = () => {
    try {
      window.localStorage.removeItem(cajaStorageKey);
    } catch (e) {}
    setCajaSeleccionada(null);
  };

  // Si bajaron la cantidad de cajas y la elegida quedó fuera de rango, re-preguntar
  useEffect(() => {
    if (
      typeof cajaSeleccionada === 'number' &&
      data.cantidad_puestos >= 1 &&
      cajaSeleccionada > data.cantidad_puestos
    ) {
      cambiarCaja();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.cantidad_puestos, cajaSeleccionada]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!apiBase) {
      setError('Link de administrador invalido.');
      return undefined;
    }

    const fetchData = () => {
      fetch(`${apiBase}/estado_general/`, {
        headers: {
          'X-Admin-Token': adminToken
        }
      })
        .then(res => res.json())
        .then(json => {
          if (json.error) {
            setError(json.error);
            setData((current) => ({
              ...current,
              ...json
            }));
            return;
          }
          setError('');
          setData(json);
        });
    };

    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [adminToken, apiBase]);

  const refrescarEstado = () => {
    return fetch(`${apiBase}/estado_general/`, {
      headers: {
        'X-Admin-Token': adminToken
      }
    })
      .then(res => res.json())
      .then(json => {
        if (!json.error) {
          setData(json);
        }
      });
  };

  const llamarSiguiente = (puesto = 1) => {
    fetch(`${apiBase}/llamar_siguiente/`, {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ puesto })
    })
      .then(res => res.json())
      .then(() => refrescarEstado());
  };

  const configurarPuestos = (cantidad) => {
    fetch(`${apiBase}/configurar/`, {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ cantidad_puestos: cantidad })
    })
      .then(res => res.json())
      .then(() => refrescarEstado());
  };

  const turnoDelPuesto = (puesto) => {
    const encontrado = (data.turnos_actuales || []).find(t => t.puesto === puesto);
    return encontrado ? encontrado.numero : null;
  };

  // Modo resto: el host llama a un grupo puntual y lo marca sentado
  const llamarGrupo = (numero) => {
    fetch(`${apiBase}/llamar/${numero}/`, {
      method: 'POST',
      headers: { 'X-Admin-Token': adminToken }
    })
      .then(res => res.json())
      .then(() => refrescarEstado());
  };

  const marcarSentado = (numero) => {
    fetch(`${apiBase}/atendido/${numero}/`, {
      method: 'POST',
      headers: { 'X-Admin-Token': adminToken }
    })
      .then(res => res.json())
      .then(() => refrescarEstado());
  };

  const editarRangos = () => {
    const actual = (data.rangos_mesa || []).join(', ');
    const entrada = window.prompt(
      'Tamaños de mesa separados por coma.\nEj: 2, 4, 6 genera las columnas 1-2, 3-4, 5-6 y 7+.',
      actual
    );
    if (entrada === null) return;

    const topes = entrada.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isInteger(n) && n > 0);
    if (topes.length === 0) {
      window.alert('Ingresá al menos un tamaño de mesa válido.');
      return;
    }

    fetch(`${apiBase}/configurar/`, {
      method: 'POST',
      headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({ rangos_mesa: topes })
    })
      .then(res => res.json())
      .then(json => {
        if (json.error) window.alert(json.error);
        refrescarEstado();
      });
  };

  const activarSubscripcion = () => {
    if (!apiBase || activating) {
      return;
    }

    setActivating(true);
    fetch(`${apiBase}/activar_subscripcion/`, {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken
      }
    })
      .then(res => res.json())
      .then(json => {
        if (json.error) {
          setError(json.error);
          return;
        }

        const redirectUrl = json.checkout_url || json.payment_provider_url || json.payment_url;
        if (redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }

        return fetch(`${apiBase}/estado_general/`, {
          headers: {
            'X-Admin-Token': adminToken
          }
        })
          .then(res => res.json())
          .then(stateJson => {
            if (!stateJson.error) {
              setData(stateJson);
            }
          });
      })
      .finally(() => {
        setActivating(false);
      });
  };

  const cancelarSubscripcion = () => {
    if (!apiBase || canceling) {
      return;
    }

    setCanceling(true);
    fetch(`${apiBase}/cancelar_subscripcion/`, {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken
      }
    })
      .then(res => res.json())
      .then(json => {
        if (json.error) {
          setError(json.error);
          return;
        }

        return fetch(`${apiBase}/estado_general/`, {
          headers: {
            'X-Admin-Token': adminToken
          }
        })
          .then(res => res.json())
          .then(stateJson => {
            if (!stateJson.error) {
              setError('');
              setData(stateJson);
            }
          });
      })
      .finally(() => {
        setCanceling(false);
      });
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Acceso invalido</h1>
        <p>{error}</p>
        {data.payment_url && <p><a href={data.payment_url}>Ir al pago</a></p>}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ ...styles.topBar, ...(isMobile ? styles.topBarMobile : {}) }}>
        <div style={styles.topBarTitleWrap}>
          <h1 style={styles.title}>📢 {data.nombre_local || 'Turnos'}</h1>
          {data.sucursal && <p style={styles.subtitle}>({data.sucursal})</p>}
        </div>

        <div style={{ ...styles.topRightActions, ...(isMobile ? styles.topActionsMobile : {}) }}>
          {data.cancelar_subscripcion_disponible && (
            <button style={styles.secondaryButton} onClick={cancelarSubscripcion} disabled={canceling || activating}>
              {canceling ? 'Procesando...' : 'Cancelar subscripcion'}
            </button>
          )}

          {data.activar_subscripcion_disponible && (
            <button style={styles.secondaryButton} onClick={activarSubscripcion} disabled={activating || canceling}>
              {activating ? 'Procesando...' : 'Activar subscripcion'}
            </button>
          )}

          {data.cancelacion_programada && (
            <p style={styles.subscriptionInfo}>
              Cancelacion programada en {data.dias_restantes_subscripcion ?? 0} dias
            </p>
          )}
        </div>
      </div>

      {data.modo === 'resto' ? (
        <div style={styles.restoWrap}>
          {(data.llamados || []).length > 0 && (
            <div style={styles.restoSeccion}>
              <h3 style={styles.restoTitulo}>📢 Llamados</h3>
              {data.llamados.map(g => (
                <div key={g.numero} style={styles.grupoRow}>
                  <span style={styles.llamadoNombre}>{g.nombre || `Turno ${g.numero}`}</span>
                  <span style={styles.grupoInfo}>👤 {g.personas} · hace {g.hace_min} min</span>
                  <button style={styles.sentadoButton} onClick={() => marcarSentado(g.numero)}>
                    ✓ Sentado
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={styles.restoSeccion}>
            <h3 style={styles.restoTitulo}>🕐 Lista de espera</h3>
            {(data.lista_espera || []).length === 0 ? (
              <p style={styles.restoVacio}>No hay grupos esperando.</p>
            ) : (
              <div style={styles.columnasGrid}>
                {(data.columnas || []).map(col => (
                  <div key={col.label} style={styles.columna}>
                    <div style={styles.columnaHeader}>
                      <span>🍽️ Mesa {col.label}</span>
                      <span style={styles.columnaCount}>{col.grupos.length}</span>
                    </div>
                    {col.grupos.length === 0 ? (
                      <p style={styles.columnaVacia}>Sin grupos</p>
                    ) : (
                      <>
                        {col.grupos.map((g, idx) => (
                          <div key={g.numero} style={{ ...styles.grupoFila, ...(idx === 0 ? styles.grupoFilaProximo : {}) }}>
                            <div style={styles.grupoNombreLinea}>
                              <span style={styles.grupoNombre}>{g.nombre || 'Sin nombre'}</span>
                              {idx === 0 && <span style={styles.proximoTag}>próximo</span>}
                            </div>
                            <span style={styles.grupoMeta}>👤 {g.personas} · {g.espera_min} min</span>
                          </div>
                        ))}
                        <button style={styles.llamarColumnaButton} onClick={() => llamarGrupo(col.grupos[0].numero)}>
                          Llamar siguiente
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={styles.rangosConfig}>
              <span style={styles.rangosLabel}>Tamaños de mesa:</span>
              <span style={styles.rangosValor}>{(data.rangos_mesa || []).join(', ')}</span>
              <button style={styles.rangosEditButton} onClick={editarRangos}>Editar</button>
            </div>
          </div>
        </div>
      ) : data.cantidad_puestos > 1 ? (
        cajaSeleccionada === null ? (
          <div style={styles.cajaSelectorCard}>
            <h2 style={styles.cajaSelectorTitulo}>¿Qué caja sos?</h2>
            <p style={styles.cajaSelectorTexto}>
              Este dispositivo va a mostrar solo el botón de esa caja, así nadie llama por la caja equivocada.
            </p>
            <div style={styles.cajaSelectorRow}>
              {Array.from({ length: data.cantidad_puestos }, (_, i) => i + 1).map(puesto => (
                <button key={puesto} style={styles.cajaSelectorButton} onClick={() => elegirCaja(puesto)}>
                  Caja {puesto}
                </button>
              ))}
            </div>
            <button style={styles.cajaSelectorTodas} onClick={() => elegirCaja('todas')}>
              Soy el dueño: ver todas las cajas
            </button>
          </div>
        ) : cajaSeleccionada === 'todas' ? (
          <div style={styles.puestosGrid}>
            {Array.from({ length: data.cantidad_puestos }, (_, i) => i + 1).map(puesto => (
              <div key={puesto} style={styles.puestoCard}>
                <h3 style={styles.puestoTitulo}>Caja {puesto}</h3>
                <p style={styles.big}>{turnoDelPuesto(puesto) || '—'}</p>
                <button style={styles.puestoButton} onClick={() => llamarSiguiente(puesto)}>
                  Llamar siguiente
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.cardsGrid}>
            <div style={styles.card}>
              <h2>Caja {cajaSeleccionada} — Turno actual</h2>
              <p style={styles.big}>{turnoDelPuesto(cajaSeleccionada) || '—'}</p>
              <button style={styles.puestoButton} onClick={() => llamarSiguiente(cajaSeleccionada)}>
                Llamar siguiente
              </button>
            </div>
          </div>
        )
      ) : (
        <div style={styles.cardsGrid}>
          <div style={styles.card}>
            <h2>Turno actual</h2>
            <p style={styles.big}>
              {data.turno_actual || "Ninguno"}
            </p>
          </div>
        </div>
      )}

      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>En espera</p>
          <p style={styles.metricValue}>{data.en_espera}</p>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Atendidos hoy</p>
          <p style={styles.metricValue}>{data.atendidos_hoy}</p>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Espera promedio</p>
          <p style={styles.metricValue}>{data.espera_promedio_min != null ? `${data.espera_promedio_min} min` : '—'}</p>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Ritmo de atención</p>
          <p style={styles.metricValue}>{data.ritmo_atencion_min != null ? `${data.ritmo_atencion_min} min/turno` : '—'}</p>
        </div>
      </div>

      {data.modo !== 'resto' && data.cantidad_puestos <= 1 && (
        <button style={styles.button} onClick={() => llamarSiguiente(1)}>
          Llamar siguiente
        </button>
      )}

      <div style={styles.footerRow}>
        {data.modo !== 'resto' && (data.cantidad_puestos <= 1 || cajaSeleccionada === 'todas') && (
          <label style={styles.puestosSelector}>
            Cajas atendiendo:
            <select
              value={data.cantidad_puestos}
              onChange={(e) => configurarPuestos(Number(e.target.value))}
              style={styles.puestosSelect}
            >
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}

        {data.modo !== 'resto' && data.cantidad_puestos > 1 && cajaSeleccionada !== null && (
          <button style={styles.cambiarCajaButton} onClick={cambiarCaja}>
            🔁 Cambiar de caja
          </button>
        )}

        {data.display_url && (
          <a href={data.display_url} target="_blank" rel="noreferrer" style={styles.footerLink}>
            📺 Pantalla para TV
          </a>
        )}
        {data.client_url && (
          <a href={data.client_url} target="_blank" rel="noreferrer" style={styles.footerLink}>
            🎟️ Página de clientes
          </a>
        )}
      </div>

    </div>
  );
}

const styles = {
  container: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
    textAlign: 'center',
    padding: '20px 16px 40px',
    fontFamily: 'Arial'
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    width: '100%',
    marginBottom: '20px'
  },
  topBarMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  },
  topBarTitleWrap: {
    textAlign: 'left',
    minWidth: 0
  },
  topRightActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    minWidth: '240px'
  },
  topActionsMobile: {
    alignItems: 'center',
    width: '100%',
    minWidth: 0
  },
  title: {
    margin: 0,
    wordBreak: 'break-word'
  },
  subtitle: {
    marginTop: '6px',
    marginBottom: 0,
    color: '#5f5f5f'
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '12px',
    width: '100%',
    maxWidth: '460px',
    margin: '0 auto'
  },
  card: {
    boxSizing: 'border-box',
    background: '#f5f5f5',
    padding: '16px',
    margin: 0,
    borderRadius: '12px',
    width: '100%'
  },
  big: {
    fontSize: '44px',
    fontWeight: 'bold'
  },
  medium: {
    fontSize: '24px'
  },
  button: {
    marginTop: '30px',
    padding: '15px 40px',
    fontSize: '20px',
    background: 'green',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    maxWidth: '100%'
  },
  secondaryButton: {
    padding: '12px 30px',
    fontSize: '18px',
    background: '#0f7b6c',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    maxWidth: '100%'
  },
  subscriptionInfo: {
    margin: 0,
    color: '#4b4b4b',
    fontSize: '13px'
  },
  puestosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    width: '100%',
    margin: '0 auto'
  },
  puestoCard: {
    boxSizing: 'border-box',
    background: '#f5f5f5',
    padding: '16px',
    borderRadius: '12px'
  },
  puestoTitulo: {
    margin: '0 0 8px 0'
  },
  puestoButton: {
    marginTop: '8px',
    padding: '12px 18px',
    fontSize: '16px',
    background: 'green',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    width: '100%'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px',
    width: '100%',
    marginTop: '16px'
  },
  metricCard: {
    background: '#eef4f2',
    borderRadius: '12px',
    padding: '12px'
  },
  metricLabel: {
    margin: 0,
    fontSize: '13px',
    color: '#4b4b4b'
  },
  metricValue: {
    margin: '6px 0 0 0',
    fontSize: '22px',
    fontWeight: 'bold'
  },
  footerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '18px',
    marginTop: '28px'
  },
  puestosSelector: {
    fontSize: '15px',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  puestosSelect: {
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid #bbb',
    fontSize: '15px'
  },
  footerLink: {
    color: '#0f7b6c',
    fontSize: '15px',
    textDecoration: 'none',
    fontWeight: 'bold'
  },
  cajaSelectorCard: {
    background: '#f5f5f5',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '460px',
    margin: '0 auto'
  },
  cajaSelectorTitulo: {
    marginTop: 0,
    marginBottom: '8px'
  },
  cajaSelectorTexto: {
    marginTop: 0,
    marginBottom: '18px',
    color: '#4b4b4b',
    fontSize: '15px'
  },
  cajaSelectorRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '12px'
  },
  cajaSelectorButton: {
    padding: '20px 28px',
    fontSize: '20px',
    fontWeight: 'bold',
    background: '#0f7b6c',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer'
  },
  cajaSelectorTodas: {
    marginTop: '18px',
    padding: '10px 16px',
    fontSize: '14px',
    background: 'transparent',
    color: '#0f7b6c',
    border: '1px solid #0f7b6c',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  cambiarCajaButton: {
    padding: '8px 14px',
    fontSize: '14px',
    background: 'transparent',
    color: '#0f7b6c',
    border: '1px solid #0f7b6c',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  restoWrap: {
    display: 'grid',
    gap: '16px',
    maxWidth: '560px',
    margin: '0 auto'
  },
  restoSeccion: {
    background: '#f5f5f5',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'left'
  },
  restoTitulo: {
    marginTop: 0,
    marginBottom: '12px'
  },
  restoVacio: {
    margin: 0,
    color: '#666'
  },
  grupoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #e3e3e3'
  },
  columnasGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: '12px',
    alignItems: 'start'
  },
  columna: {
    background: '#fff',
    borderRadius: '10px',
    padding: '10px',
    minHeight: '60px'
  },
  columnaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    paddingBottom: '8px',
    borderBottom: '2px solid #0f7b6c',
    marginBottom: '8px',
    color: '#0f7b6c'
  },
  columnaCount: {
    background: '#0f7b6c',
    color: '#fff',
    borderRadius: '999px',
    minWidth: '22px',
    textAlign: 'center',
    padding: '1px 7px',
    fontSize: '13px'
  },
  columnaVacia: {
    margin: '6px 0',
    color: '#bbb',
    textAlign: 'center',
    fontSize: '13px'
  },
  grupoFila: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px',
    marginBottom: '6px',
    background: '#f5f7f6',
    borderRadius: '8px'
  },
  grupoFilaProximo: {
    background: '#e3f3ee',
    border: '1px solid #0f7b6c'
  },
  grupoNombreLinea: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '6px'
  },
  grupoNombre: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#222',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  grupoMeta: {
    fontSize: '13px',
    color: '#666'
  },
  proximoTag: {
    fontSize: '9px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#fff',
    background: '#0f7b6c',
    padding: '2px 6px',
    borderRadius: '999px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  grupoInfo: {
    flex: 1,
    color: '#444',
    fontSize: '15px'
  },
  llamadoNombre: {
    fontSize: '18px',
    fontWeight: 'bold',
    minWidth: '90px'
  },
  llamarColumnaButton: {
    marginTop: '8px',
    padding: '10px 0',
    fontSize: '15px',
    background: 'green',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%'
  },
  llamarColumnaButtonDisabled: {
    background: '#cbd5d0',
    cursor: 'default'
  },
  llamarGrupoButton: {
    padding: '8px 0',
    fontSize: '15px',
    background: 'green',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%'
  },
  rangosConfig: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #e3e3e3',
    fontSize: '14px',
    color: '#555'
  },
  rangosLabel: {
    fontWeight: 'bold'
  },
  rangosValor: {
    flex: 1
  },
  rangosEditButton: {
    padding: '6px 12px',
    fontSize: '13px',
    background: 'transparent',
    color: '#0f7b6c',
    border: '1px solid #0f7b6c',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  sentadoButton: {
    padding: '10px 18px',
    fontSize: '15px',
    background: '#0f7b6c',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer'
  }
};

export default Admin;
